// Where the reader gets bytes from, and where an extracted entry goes. The
// archive is read in place, a range at a time; it is never loaded whole, so a
// multi-gigabyte download fits on a phone.

export interface ByteSource {
  readonly size: number;
  /** Exactly `length` bytes at `offset`, or a rejection. */
  read(offset: number, length: number): Promise<Uint8Array>;
  close(): void;
}

export interface ByteSink<T> {
  write(chunk: Uint8Array): void | Promise<void>;
  finish(): Promise<T>;
  /** Discards anything written; called on any failure or cancellation. */
  abort(): void;
}

export function bytesSource(bytes: Uint8Array): ByteSource {
  return {
    size: bytes.length,
    async read(offset, length) {
      if (offset < 0 || length < 0 || offset + length > bytes.length) throw new RangeError('read outside the archive');
      return bytes.subarray(offset, offset + length);
    },
    close() {},
  };
}

/** The web: a File or Blob the person picked; `slice` reads only the range. */
export function blobSource(blob: Blob): ByteSource {
  return {
    size: blob.size,
    async read(offset, length) {
      if (offset < 0 || length < 0 || offset + length > blob.size) throw new RangeError('read outside the archive');
      return new Uint8Array(await blob.slice(offset, offset + length).arrayBuffer());
    },
    close() {},
  };
}

export function memorySink(): ByteSink<Uint8Array> {
  let parts: Uint8Array[] = [];
  return {
    write(chunk) { parts.push(chunk.slice()); },
    async finish() {
      const total = parts.reduce((sum, part) => sum + part.length, 0);
      const out = new Uint8Array(total);
      let at = 0;
      for (const part of parts) { out.set(part, at); at += part.length; }
      parts = [];
      return out;
    },
    abort() { parts = []; },
  };
}

/** The web: the extracted entry becomes a Blob ready for the upload pipeline. */
export function blobSink(type: string): ByteSink<Blob> {
  let parts: Uint8Array[] = [];
  return {
    write(chunk) { parts.push(chunk.slice()); },
    async finish() {
      // React Native's Blob typing knows only strings and Blobs; the browser's
      // Blob, the only one this runs on, takes byte arrays as well.
      const blob = new Blob(parts as unknown as Blob[], { type, lastModified: Date.now() });
      parts = [];
      return blob;
    },
    abort() { parts = []; },
  };
}
